import { useId, useState, type ChangeEvent } from 'react';
import { Stack } from '@mui/material';
import { SwitchLayout, FloatingLabelInputLayout } from 'framework/ui/layout';
import { AUTHOR_TEXTS } from '../../texts/author';
import type { SettingsPanelProps } from '../card';
import { FormatMessageSettingsPanel } from '../shared/FormatMessageSettingsPanel';
import { numberText, valueOfText } from '../shared/numberText';
import type { NumberStepperSettings } from './settings';

export const NumberStepperSettingsPanel = (props: SettingsPanelProps<NumberStepperSettings>) => {
  const { settings, onChange } = props;
  return (
    <Stack useFlexGap spacing={2}>
      <StepInput step={settings.step ?? 1} onStep={step => onChange({ step })} />
      <SwitchLayout
        source="integerOnly"
        label="Whole numbers only"
        value={settings.integerOnly ?? false}
        onChangeCallback={onChange}
        sxSwitch={{ ml: 2 }}
      />
      <FormatMessageSettingsPanel {...props} />
    </Stack>
  );
};

interface StepInputProps {
  step: number;
  onStep: (step: number | undefined) => void;
}

const StepInput = ({ step, onStep }: StepInputProps) => {
  const id = useId();
  const [draft, setDraft] = useState<{ step: number; text: string } | null>(null);
  const text = draft?.step === step ? draft.text : numberText(step);
  const typed = valueOfText(text);
  const saved = typed === null || (typeof typed === 'number' && typed > 0);
  return (
    <FloatingLabelInputLayout
      source={id}
      label="Step of the − and + buttons"
      placeholder="1"
      value={text}
      errorMessages={saved ? [] : [AUTHOR_TEXTS.settingsPanels.stepNotSaved]}
      sx={{ mb: 0 }}
      slotProps={{
        inputLabel: { shrink: true },
        htmlInput: {
          inputMode: 'decimal',
          autoComplete: 'off',
          placeholder: '1',
          onChange: (event: ChangeEvent<HTMLInputElement>) => {
            const nextText = event.currentTarget.value;
            const next = valueOfText(nextText);
            const valid = typeof next === 'number' && next > 0;
            setDraft({ step: next === null ? 1 : valid ? next : step, text: nextText });
            if (next === null) onStep(undefined);
            else if (valid) onStep(next);
          },
        },
      }}
    />
  );
};
